if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.variantChangeUnsubscriber = undefined;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        // Cleanup variant change subscription
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }

        // Cleanup fibbl:unavailable listener
        this.fibblAbortController?.abort();

        // Cleanup lightbox (reset the reference so a re-init after a
        // reconnect creates a fresh instance).
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);

        // When Fibbl confirms a product has no 3D model, fibbl-gallery-sync
        // bubbles `fibbl:unavailable` up to here so we can drop the empty slide
        // from the Swiper (otherwise autoHeight collapses the mobile gallery).
        // init() runs again on variant changes (product-info.js), so abort a
        // previous listener before registering a new one.
        this.fibblAbortController?.abort();
        this.fibblAbortController = new AbortController();
        this.addEventListener('fibbl:unavailable', () => this.removeFibblSlide(), {
          signal: this.fibblAbortController.signal,
          once: true,
        });

        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        // Guard against re-init (variant changes): one lightbox and one
        // subscription per gallery, otherwise they stack up on every change.
        if (this.enableImageZoom && !this.lightbox) {
          this.initImageZoom();

          // Subscribe to variant change events to update PhotoSwipe dataSource
          this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            this.handleVariantChange.bind(this)
          );
        }
      }

      handleVariantChange({ data: { variant } }) {
        if (!variant) return;

        // Update PhotoSwipe dataSource with new variant images
        this.updatePhotoSwipeDataSource();
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'bullets',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      // Removes the optimistically-rendered Fibbl slide once Fibbl reports the
      // product has no 3D model. Removing it from both the Swiper and the DOM
      // prevents the empty slide from collapsing the mobile gallery height
      // (autoHeight) and stops a later re-init from re-adding it.
      removeFibblSlide() {
        // Drop the 3D thumbnail (separate thumbs slider or static markup).
        const fibblThumb = this.querySelector('.product__thumbs-item[data-target="fibbl"]');
        if (fibblThumb) {
          fibblThumb.remove();
          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.update();
          }
        }

        // Keep the cached media items in sync so a variant change / re-init
        // doesn't re-insert the slide.
        if (Array.isArray(this.elements.mediaItems)) {
          this.elements.mediaItems = this.elements.mediaItems.filter(
            (item) => item.dataset.mediaId !== 'fibbl'
          );
        }

        const fibblSlide = this.elements.mediaList?.querySelector(
          '.product__media-item[data-media-id="fibbl"]:not(.swiper-slide-duplicate)'
        );
        if (!fibblSlide) return;

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          // Swiper's Manipulation module (removeSlide) isn't part of the
          // vendor bundle, so mirror its behavior manually: tear the loop
          // down, drop the node, rebuild the loop and restore the position.
          const swiper = this.sliderInstance.slider;
          const realSlides = [
            ...this.elements.mediaList.querySelectorAll(
              '.product__media-item:not(.swiper-slide-duplicate)'
            ),
          ];
          const removedIndex = realSlides.indexOf(fibblSlide);
          const activeRealIndex = swiper.realIndex || 0;
          const newRealIndex = removedIndex < activeRealIndex ? activeRealIndex - 1 : activeRealIndex;
          const maxRealIndex = Math.max(realSlides.length - 2, 0);
          const targetIndex = Math.min(Math.max(newRealIndex, 0), maxRealIndex);

          if (swiper.params.loop) swiper.loopDestroy();
          fibblSlide.remove();
          if (swiper.params.loop) swiper.loopCreate();
          swiper.update();

          if (swiper.params.loop) {
            swiper.slideToLoop(targetIndex, 0, false);
          } else {
            swiper.slideTo(targetIndex, 0, false);
          }
          swiper.updateAutoHeight?.(0);

          // The removed Fibbl slide was usually the active one with touch
          // disabled (drag-to-rotate). slideToLoop to the same realIndex
          // fires no realIndexChange, so re-evaluate the draggable state
          // against the slide that is active now.
          const activeSlide = swiper.slides[swiper.activeIndex];
          if (activeSlide) {
            this.toggleSliderDraggableState(!this.isRotatableMedia(activeSlide));
          }
        } else {
          // No active Swiper (e.g. desktop grid layout) — just drop the node.
          fibblSlide.remove();
        }

        this.syncProductMediaClasses();
      }

      // Liquid computes the product--media-has-* / product--odd-media classes
      // including the Fibbl slide (they drive the desktop grid layouts via
      // nth-child patterns). Recompute them after the slide is removed.
      syncProductMediaClasses() {
        const productEl = this.closest('.product');
        if (!productEl) return;

        const count = this.querySelectorAll(
          '.product__media-item:not(.swiper-slide-duplicate)'
        ).length;

        [...productEl.classList]
          .filter((className) => /^product--media-has-\d+$/.test(className))
          .forEach((className) => productEl.classList.remove(className));

        productEl.classList.add(`product--media-has-${count}`);
        productEl.classList.toggle('product--media-has-many', count > 1);
        if (this.mediaLayout === 'grid-mix') {
          const modulo = count % 3;
          productEl.classList.toggle('product--odd-media', modulo === 2);
        }
      }

      // PhotoSwipe's dataSource and variant featured_media positions don't
      // include the prepended Fibbl slide; Swiper's real indexes do.
      get fibblSlideOffset() {
        const hasFibblSlide = this.elements.mediaList?.querySelector(
          '.product__media-item[data-media-id="fibbl"]:not(.swiper-slide-duplicate)'
        );
        return hasFibblSlide ? 1 : 0;
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      generateDataSource() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }
        return dataSource;
      }

      initImageZoom() {
        const dataSource = this.generateDataSource();

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            // PhotoSwipe indexes exclude the Fibbl slide, Swiper includes it.
            const targetIndex = currIndex + this.fibblSlideOffset;
            if (this.sliderInstance.slider.realIndex !== targetIndex) {
              this.sliderInstance.slider.slideToLoop(targetIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      updatePhotoSwipeDataSource() {
        if (!this.lightbox) return;

        const newDataSource = this.generateDataSource();
        this.lightbox.options.dataSource = newDataSource;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            this.toggleSliderDraggableState(!this.isRotatableMedia(slides[activeIndex]));
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            // The thumbs slider has no loop duplicates — use the real index.
            thumbs.swiper.slideTo(realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);
            this.toggleSliderDraggableState(!this.isRotatableMedia(slides[activeIndex]));
          }
        });
      }

      // 3D media (Shopify model viewer & Fibbl) use drag to rotate the model,
      // so the slider must not hijack the gesture. Navigation stays available
      // via pagination bullets / thumbnails.
      isRotatableMedia(slide) {
        return slide.dataset.mediaType === 'model' || slide.dataset.mediaType === 'fibbl';
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.sliderInstance.slider) {
          // featured_media.position is 1-based within product.media; the
          // prepended Fibbl slide shifts all slider indexes by one.
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1 + this.fibblSlideOffset);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        // The Fibbl slide has no data-media-index and stays pinned in front —
        // re-appending its node would reconnect <fibbl-model-viewer> and
        // reload the 3D model on every variant change.
        const newMedias = this.elements.mediaItems.filter(
          (media) => media.dataset.mediaId !== 'fibbl'
        );

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        // appendChild moves already-connected nodes, so this reorders the
        // list in place without wiping the wrapper.
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
